import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';

export type RecipientDraft = {
  email: string;
  firstName: string;
};

type RecipientFieldsProps = {
  value: RecipientDraft;
  onChange: (next: RecipientDraft) => void;
  /** Keyed by draft field; absent means the field is valid so far. */
  errors: Partial<Record<keyof RecipientDraft, string>>;
};

/**
 * Who the card is going to.
 *
 * First name only. The pre-spec asked for a last name too, but nothing consumes
 * one — the email greets by first name and SendGrid's `to` is a bare address —
 * so it was collected, validated and dropped. In a feature whose whole design
 * principle is that nothing about the recipient is recorded, asking for a
 * surname with nowhere to put it is the wrong default.
 */
export function RecipientFields({ value, onChange, errors }: RecipientFieldsProps) {
  const set = (field: keyof RecipientDraft) => (event: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [field]: event.target.value });

  return (
    <>
      <div className="min-w-0 space-y-1.5">
        <Label htmlFor="recipient-email">Recipient’s email</Label>
        <Input
          id="recipient-email"
          type="email"
          autoComplete="email"
          value={value.email}
          onChange={set('email')}
          aria-invalid={Boolean(errors.email)}
          aria-describedby={errors.email ? 'recipient-email-error' : undefined}
        />
        {errors.email && (
          <p id="recipient-email-error" className="text-sm text-destructive">
            {errors.email}
          </p>
        )}
      </div>

      <div className="min-w-0 space-y-1.5">
        <Label htmlFor="recipient-first-name">First name</Label>
        <Input
          id="recipient-first-name"
          type="text"
          autoComplete="given-name"
          value={value.firstName}
          onChange={set('firstName')}
          aria-invalid={Boolean(errors.firstName)}
          aria-describedby={errors.firstName ? 'recipient-first-name-error' : undefined}
        />
        {errors.firstName && (
          <p id="recipient-first-name-error" className="text-sm text-destructive">
            {errors.firstName}
          </p>
        )}
      </div>
    </>
  );
}
